import _ from "lodash";
import moment from "moment";
import { getFormSummary } from "./getFormSummary";
import { ReportGeneratorUtils } from "./reportGeneratorUtils";
import { getFormOverrides } from "./getFormOverrides";
import { guid } from "../../../utils/UUID";
import { versionInfo } from "../../MainPage/VersionInfo";
import cloneDeep from "lodash/cloneDeep";

export const ReportGroupingUtils = {
  /**
   *
   * @param {date}  sourceDate The main date with which we want to compare
   * @param {date} targetDate The date with which we want to compare
   * @returns {boolean} returns True if the date is same, otherwise returns False
   */
  areDatesTheSame(sourceDate, targetDate) {
    return moment(sourceDate).isSame(targetDate, "day");
  },
  /**
   *
   * @param {array} reportData
   * @param {boolean} desc indicates whether the reports need to be returned in descending order or not.
   * @returns array of reports grouped by inspector name and date of the inspection
   */
  groupReportsByMonth(reports) {
    let testCodeMonthMap = {};

    reports.forEach((report) => {
      const { date, testCode, formData } = report;
      const monthKey = `${new Date(date).getMonth() + 1}`; // Generate month key (e.g., "2024-5")
      const testCodeMonth = `${testCode}-${monthKey}`; // Generate unique identifier for testCode in a specific month

      if (!testCodeMonthMap[testCodeMonth] || date > testCodeMonthMap[testCodeMonth].date) {
        // Update if no entry for this testCode in this month or found a later date
        testCodeMonthMap[testCodeMonth] = {
          ...report,
        };
      }
    });

    // Convert the testCodeMonthMap back to an array of reports
    let uniqueReports = Object.values(testCodeMonthMap);

    let groupedData = {};

    uniqueReports.forEach((report) => {
      const monthKey = `${new Date(report.date).getMonth() + 1}`; // Generate month key (e.g., "2024-5")
      const yearKey = `${new Date(report.date).getFullYear()}`;
      if (!groupedData[monthKey]) {
        // Initialize if the monthKey is encountered for the first time
        groupedData[monthKey] = {
          year: yearKey,
          month: monthKey,
          summary: {},
          reports: [],
          fraHeaderInfo: this.getFraHeaderInfo(report, {}),
        };
      }

      // Push formData to the grouped month
      // summary is only applicable if all the forms combined have unique ids
      let summary = getFormSummary({ form: report.formData }, "id");
      let testUser = report.user && report.user.name;
      let testDate = report.date;
      let testBackDate = report.backdate && report.backdate.originalDate;

      for (let prop in summary) {
        if (summary.hasOwnProperty(prop)) {
          let userPropName = `user${prop}`;
          let datePropName = `date${prop}`;
          let backDatePropName = `backdate${prop}`;
          summary[userPropName] = testUser;
          summary[datePropName] = testDate;
          testBackDate && (summary[backDatePropName] = testBackDate);
        }
      }
      groupedData[monthKey].summary = { ...groupedData[monthKey].summary, ...summary };
      // Push the original report to the grouped month
      groupedData[monthKey].reports.push(report);
    });

    // Convert groupedData object to an array of grouped months
    groupedData = Object.values(groupedData);

    return groupedData;
  },

  groupReportsByDateAndUser(reportData, desc) {
    let isReVAMP = versionInfo.isReVAMP();
    let sortedData = _.cloneDeep(reportData);
    if (isReVAMP) {
      sortedData = (reportData || []).sort(
        (a, b) => new Date((b.otherInfo || {}).datePerformed || b.date) - new Date((a.otherInfo || {}).datePerformed || a.date),
      );
    }
    let reportGroups = sortedData.reduce((reportGroups, report) => {
      let i = reportGroups.findIndex((g) => report.user && report.user.email === g.email && this.areDatesTheSame(g.date, report.date));
      if (i === -1) {
        let fraHeaderInfo = {};
        reportGroups.push({
          date: report.date,
          email: report.user.email,
          fraHeaderInfo: this.getFraHeaderInfo(report, fraHeaderInfo),
          reports: [report],
        });
      } else {
        reportGroups[i].reports.push(report);
        reportGroups[i].fraHeaderInfo = this.getFraHeaderInfo(report, reportGroups[i].fraHeaderInfo);
      }
      return reportGroups;
    }, []);
    if (desc) {
      reportGroups.reverse();
    }

    return reportGroups;
  },

  groupReportsByDateAndUserAndInspection(reportData, desc) {
    let reportGroups = reportData.reduce((reportGroups, report) => {
      let i = reportGroups.findIndex(
        (g) =>
          report.user &&
          report.user.email === g.email &&
          this.areDatesTheSame(g.date, report.date) &&
          report.inspectionId === g.inspectionId,
      );
      if (i === -1) {
        let fraHeaderInfo = {};
        reportGroups.push({
          date: report.date,
          email: report.user.email,
          inspectionId: report.inspectionId,
          fraHeaderInfo: this.getFraHeaderInfo(report, fraHeaderInfo),
          reports: [report],
        });
      } else {
        reportGroups[i].reports.push(report);
        reportGroups[i].fraHeaderInfo = this.getFraHeaderInfo(report, reportGroups[i].fraHeaderInfo);
      }
      return reportGroups;
    }, []);
    if (desc) {
      reportGroups.reverse();
    }
    return reportGroups;
  },

  groupReportsOnly(reportData, desc) {
    let reportGroups = reportData.reduce((reportGroups, report, i) => {
      let theOnlyIndex = 0;

      if (i === 0) {
        let fraHeaderInfo = {};
        reportGroups.push({
          date: report.date,
          email: report.user.email,
          fraHeaderInfo: this.getFraHeaderInfo(report, fraHeaderInfo),
          reports: [report],
        });
      } else {
        reportGroups[theOnlyIndex].reports.push(report);
      }
      return reportGroups;
    }, []);
    if (desc) {
      reportGroups.reverse();
    }
    return reportGroups;
  },

  /**
   *
   * @param {object} testSched inspection report complete object.
   * @param {object} fraHeaderInfo previous FRA header info that needs to be iteratively filled. pass the previous header info here.
   * @returns updated FRA Header info.
   */
  getFraHeaderInfo(testSched, fraHeaderInfo) {
    !fraHeaderInfo && (fraHeaderInfo = {});
    !fraHeaderInfo.location && (fraHeaderInfo.lineName = testSched.lineName);
    !fraHeaderInfo.mp && (fraHeaderInfo.mp = testSched.assetMP);
    !fraHeaderInfo.assetType && (fraHeaderInfo.assetType = testSched.assetType);
    !fraHeaderInfo.assetUnitId && (fraHeaderInfo.assetUnitId = testSched.assetName);
    !fraHeaderInfo.lineId && (fraHeaderInfo.lineId = testSched.lineId);
    !fraHeaderInfo.lineName && (fraHeaderInfo.lineId = testSched.Name);
    !fraHeaderInfo.date && (fraHeaderInfo.date = testSched.date && moment(testSched.date).format("MM-DD-YYYY"));
    !fraHeaderInfo.entryDate &&
      (fraHeaderInfo.entryDate =
        testSched.backdate && testSched.backdate.originalDate && moment(testSched.backdate.originalDate).format("MM-DD-YYYY"));
    !fraHeaderInfo.userName && (fraHeaderInfo.userName = testSched.user && testSched.user.name);
    !fraHeaderInfo.email && (fraHeaderInfo.email = testSched.user && testSched.user.email);
    !fraHeaderInfo.signatureImage &&
      (fraHeaderInfo.signatureImage = testSched.user && testSched.user.signature && testSched.user.signature.imgName);

    !fraHeaderInfo.certifiedPerformedDate &&
      (fraHeaderInfo.certifiedPerformedDate =
        testSched.otherInfo &&
        testSched.otherInfo.certifiedBy &&
        testSched.otherInfo.datePerformed &&
        moment(testSched.otherInfo.datePerformed).format("MM-DD-YYYY"));

    !fraHeaderInfo.certifiedBy && (fraHeaderInfo.certifiedBy = testSched.otherInfo && testSched.otherInfo.certifiedBy);

    return fraHeaderInfo;
  },
  /**
   *
   * @param {array} reportsByDateAndUser array of reports grouped by inspector name and date of the inspection
   * @param {array} testCodes array of fra test codes for which we need to filter the reports
   * @returns returns the list of reports filtered by the provided fra test codes
   */
  filterReportsByTestCodes(reportsByDateAndUser, testCodes) {
    return reportsByDateAndUser.map((group) => {
      let g = { ...group };
      let reports = [];
      testCodes.forEach((testCode) => {
        reports = [...reports, ...g.reports.filter((r) => r.testCode === testCode)];
      });
      g.reports = reports;
      return g;
    });
  },
  RemoveDuplicateTestCodesFromGroupedReport(reports) {
    if (reports.length == 0) return [];
    let reps = JSON.parse(JSON.stringify(reports));
    let returnReports = [];
    reps.forEach((e) => {
      let GroupedReport = { ...e, reports: [] };
      let addedTEstCodes = [];
      e.reports.forEach((r) => {
        if (!addedTEstCodes.includes(r.testCode)) {
          GroupedReport.reports.push(r);
          addedTEstCodes.push(r.testCode);
        }
      });
      returnReports.push(GroupedReport);
    });
    return returnReports;
  },
  filterReportsByTestCodesAndUpdateDuplicateTestCodes(reportsByDateAndUser, testCodes) {
    return reportsByDateAndUser.map((group) => {
      let g = { ...group };
      let reports = [];
      let groupReports = g.reports;

      groupReports.forEach((report) => {
        let foundTest = testCodes.find((test) => test === report.testCode);
        if (foundTest) {
          let index = reports.findIndex((t) => t.testCode === report.testCode);

          if (index === -1) {
            reports.push(report);
          }
        }
      });
      g.reports = reports;
      return g;
    });
  },

  addFormOverridesToSummary(summary, formOverrides) {
    //add formOverrides property to summary
    summary.formOverrides = formOverrides;
    //add formOverrides property to summary table
    Object.keys(summary).forEach((key) => {
      const value = summary[key];

      // Check if the property is an array
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          // Ensure item is an object before adding formOverrides
          if (typeof item === "object" && item !== null) {
            let formOverridesObj = formOverrides[key];
            item.formOverrides = formOverridesObj[index];
          }
        });
      }
    });
  },

  decodeTestsInReportGroups(reportsByDateAndUser, keyAsLabel) {
    return reportsByDateAndUser.map((report) => {
      report.decodedData = report.reports.reduce((data, r) => {
        let childForms = r.childForms;
        let childFormSummary = [];
        if (childForms) {
          for (let cf of childForms) {
            let formData = cf.form;
            let cfSummary = getFormSummary({ form: formData }, keyAsLabel ? "label" : "id");
            childFormSummary.push({ ...cf, childFormSummary: cfSummary });
          }
        }
        let dataObj = {
          summary: getFormSummary({ form: r.formData }, keyAsLabel ? "label" : "id"),
          ...r,
        };
        if (childFormSummary && childFormSummary.length > 0) {
          dataObj.childFormWithSummary = childFormSummary;
        }
        const formOverrides = getFormOverrides({ form: r.formData }, keyAsLabel ? "label" : "id");
        ReportGroupingUtils.addFormOverridesToSummary(dataObj.summary, formOverrides);
        data.push(dataObj);
        return data;
      }, []);
      return report;
    });
  },

  injectUserInfoInReportData(reportData, userList) {
    let dataToReturn = _.cloneDeep(reportData);

    return dataToReturn.map((data) => {
      let formDataWithUserDetails = data.formData.map((field) => {
        if (field.type === "table") {
          field.value = field.value.map((v) => {
            v.form = v.form.map((formVal) => {
              let user = userList && userList.find((u) => u._id.toString() === formVal.userId);
              return {
                ...formVal,
                ...(user && { userName: user.name, userSignature: user.signature, userBadge: user.employeeNumber }),
              };
            });

            return v;
          });
        } else {
          let user = userList && userList.find((u) => u._id.toString() === field.userId);
          field = {
            ...field,
            ...(user && { userName: user.name, userSignature: user.signature, userBadge: user.employeeNumber }),
          };
        }

        return field;
      });

      let injectedUserInfoFormData = ReportGroupingUtils.injectInspectorFormDataFields(formDataWithUserDetails);
      data.formData = injectedUserInfoFormData;
      return data;
    });
  },

  filterReviewRequestedGroups(decodedTestGroups, reportType) {
    return decodedTestGroups
      .map((report, i) => {
        const allReviewReqTests =
          report && Array.isArray(report.reports) && report.reports.length > 0
            ? report.reports.filter((test) => test.otherInfo && test.otherInfo.reviewReq)
            : [];

        const completedReviewReqTests =
          report && Array.isArray(report.reports) && report.reports.length > 0
            ? report.reports.filter(
                (test) =>
                  test.otherInfo && test.otherInfo.reviewReq && test.otherInfo.supervisorNotes && test.otherInfo.supervisorNotes.length > 0,
              )
            : [];

        const pendingReviewReqTests = allReviewReqTests.length > 0 && completedReviewReqTests.length == 0 ? allReviewReqTests : [];

        const skipReviewTests =
          report && Array.isArray(report.reports) && report.reports.length > 0
            ? report.reports.filter((test) => test.skipReviewFilter)
            : [];

        let reportToReturn = { ...report, reviewReqTests: allReviewReqTests };
        if (reportType === "All Reviews" && allReviewReqTests.length > 0) {
          return reportToReturn;
        } else if (reportType === "Pending Review" && pendingReviewReqTests.length > 0) {
          return reportToReturn;
        } else if (reportType === "Completed Review" && completedReviewReqTests.length > 0) {
          return reportToReturn;
        } else if (skipReviewTests.length > 0) {
          return reportToReturn;
        } else if (reportType === "All") {
          return reportToReturn;
        }
      })
      .filter(Boolean);
  },

  combineMultipleUserReportDataByDate(reportData, groupByDate = true, skipFilledByFields) {
    // 1. Sort by date ascending
    if (!reportData) return [];
    if (!reportData.length) return [];

    reportData.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Determine how to group the data
    const grouped = groupByDate
      ? reportData.reduce((acc, data) => {
          const dateKey = moment(data.date).format("MM-DD-YYYY");
          if (!acc[dateKey]) acc[dateKey] = [];
          acc[dateKey].push(data);
          return acc;
        }, {})
      : { all: reportData };

    // Process each group (either by date or just 'all')
    const result = Object.entries(grouped).map(([date, group]) => {
      const hardCoFields = group
        .map((entry) => entry.formData.filter((field) => field.accessible === false).map((field) => field.id))
        .reduce((acc, ids) => acc.filter((id) => ids.includes(id)));

      const combinedData = group.reduce((acc, data) => {
        const { formData, user, _id } = data;

        if (!acc.formData) acc.formData = [];
        if (!acc.filledByUserList) acc.filledByUserList = [];
        if (!acc.testSchedulesList) acc.testSchedulesList = [];

        // 2. Filter and attach user
        const accessibleFormFields = formData
          .filter((f) => f.accessible !== false || hardCoFields.includes(f.id))
          .map((gd) => {
            if (gd.type === "table" && Array.isArray(gd.value)) {
              return {
                ...gd,
                value: gd.value.map((row) => ({
                  ...row,
                  userName: user.name,
                  userSignature: user.signature,
                })),
              };
            } else if (gd.id) {
              return {
                ...gd,
                userName: user.name,
                userSignature: user.signature,
              };
            }
            return gd;
          });
        // 3. Merge logic
        const mergedFormData = [...acc.formData];
        accessibleFormFields.forEach((newField) => {
          const existingIndex = mergedFormData.findIndex((f) => f.id === newField.id);
          if (existingIndex !== -1) {
            const existingField = mergedFormData[existingIndex];
            if (newField.type === "table" && Array.isArray(newField.value) && Array.isArray(existingField.value)) {
              mergedFormData[existingIndex] = {
                ...existingField,
                value: [...existingField.value, ...newField.value],
              };
            } else {
              mergedFormData[existingIndex] = newField;
            }
          } else {
            mergedFormData.push(newField);
          }
        });

        acc.formData = mergedFormData;
        if (!acc.filledByUserList.some((u) => u.id === user.id)) {
          acc.filledByUserList.push(user);
        }
        acc.testSchedulesList.push(_id.toString());

        return acc;
      }, {});

      // 4. Copy non-form metadata from the first item
      const lastItem = group[group.length - 1];
      Object.keys(lastItem).forEach((key) => {
        if (key !== "formData" && key !== "filledByUserList" && key !== "testSchedulesList") {
          combinedData[key] = lastItem[key];
        }
      });

      // 5. Inject inspector fields
      if (!skipFilledByFields) {
        combinedData.formData = ReportGroupingUtils.injectInspectorFormDataFields(combinedData.formData);
      }

      return combinedData;
    });
    // 6. Show latest report first
    return result.sort((a, b) => new Date(b.date) - new Date(a.date));
  },

  injectInspectorFormDataFields(formData) {
    /**
    @param {id} string,          // Required: Unique identifier of the form field
    @param {type?} string,       // Optional: Field type, e.g., "table"
    @param {userName} string,    // Required: Name of the user who filled the field
    @param {value?} any          // Optional: Field value; for tables, this is an array of rows
      **/
    let updatedFormData = _.cloneDeep(formData);
    formData.forEach((d) => {
      if (d.id && d.accessible !== false) {
        if (d.type === "table") {
          let tableField = updatedFormData.find((td) => td.id === d.id);
          let tableInspectorValueField =
            tableField.value &&
            tableField.value.length &&
            tableField.value.reduce((acc, v) => {
              if (!v.removed && v.form) {
                v.form = v.form.reduce((res, formVal) => {
                  res.push(
                    { ...formVal },
                    { id: `${formVal.id}_filledBy`, value: formVal.userName },
                    { id: `${formVal.id}_filledBySignature`, value: formVal.userSignature },
                    { id: `${formVal.id}_filledByBadge`, value: formVal.userBadge },
                  );

                  return res;
                }, []);
                acc.push({
                  ...v,
                  form: [
                    ...v.form,
                    { id: `${d.id}_filledBy`, value: v.userName },
                    { id: `${d.id}_filledBySignature`, value: v.userSignature },
                    { id: `${d.id}_filledByBadge`, value: v.userBadge },
                  ],
                });
              }
              return acc;
            }, []);
          tableField.value = tableInspectorValueField;
        } else {
          updatedFormData.push({ id: `${d.id}_filledBy`, value: d.userName });
          updatedFormData.push({ id: `${d.id}_filledBySignature`, value: d.userSignature });
          updatedFormData.push({ id: `${d.id}_filledByBadge`, value: d.userBadge });
        }
      }
    });
    return updatedFormData;
  },

  convertToTableField(reportData = [], fieldsToConvert = [], newId = "") {
    return reportData.map((data) => {
      const { formData, user } = data;
      let key = guid();
      let formFields = [];

      // Extract fields to convert
      formData.forEach((d) => {
        if (fieldsToConvert.includes(d.id)) {
          formFields.push(d);
        }
      });

      let newFormData = _.cloneDeep(formData);
      formFields.length > 0 &&
        newFormData.push({
          id: newId,
          value: [{ user: user, form: formFields, key: key, id: key }],
          type: "table",
        });

      return {
        ...data,
        formData: newFormData,
      };
    });
  },
};



// WEBPACK FOOTER //
// ./src/components/Reports/utils/reportGroupingUtils.js